import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../../config/config';
import { showToast } from '../../components/utils/Toast';

// Fetch app settings
export const fetchAppSettings = createAsyncThunk(
  'settings/fetchAppSettings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(`${config.apiUrl}/api/settings/app`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to fetch app settings');
      }
      
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Update app settings
export const updateAppSettings = createAsyncThunk(
  'settings/updateAppSettings',
  async (settings, { rejectWithValue }) => {
    try {
      const response = await fetch(`${config.apiUrl}/api/settings/app`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(settings)
      });
      
      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to update app settings');
      }
      
      showToast('Settings updated successfully', 'success');
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Fetch users with pagination
export const fetchUsers = createAsyncThunk(
  'settings/fetchUsers',
  async ({ page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      console.log('Fetching users with:', { page, limit });
      const response = await fetch(
        `${config.apiUrl}/api/settings/users?page=${page}&limit=${limit}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      const data = await response.json();
      console.log('Users API response:', data);

      if (!response.ok) {
        console.error('Users API error:', data);
        return rejectWithValue(data.message);
      }

      return {
        users: data.users,
        pagination: data.pagination
      };
    } catch (error) {
      console.error('Users fetch error:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Delete user
export const deleteUser = createAsyncThunk(
  'settings/deleteUser',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${config.apiUrl}/api/users/${userId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message);
      }

      return { userId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Update app logo
export const updateAppLogo = createAsyncThunk(
  'settings/updateAppLogo',
  async (file, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('logo', file);

      const response = await fetch(
        `${config.apiUrl}/api/settings/logo`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: formData
        }
      );

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Banner Message actions
export const fetchBannerMessage = createAsyncThunk(
  'settings/fetchBannerMessage',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${config.apiUrl}/api/settings/banner-message`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message);
      }

      return data.bannerMessage;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateBannerMessage = createAsyncThunk(
  'settings/updateBannerMessage',
  async (bannerData, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${config.apiUrl}/api/settings/banner-message`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(bannerData)
        }
      );

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message);
      }

      return data.bannerMessage;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Add these new actions
export const fetchCostGroupsData = createAsyncThunk(
  'settings/fetchCostGroupsData',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${config.apiUrl}/api/settings/cost-groups`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message);
      }

      return data.costGroups;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCostCodesData = createAsyncThunk(
  'settings/fetchCostCodesData',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${config.apiUrl}/api/settings/cost-codes`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message);
      }

      return data.costCodes;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Add new action
export const updateUser = createAsyncThunk(
  'settings/updateUser',
  async ({ id, updates }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${config.apiUrl}/api/users/${id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(updates)
        }
      );

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Add new action
export const changeUserPassword = createAsyncThunk(
  'settings/changeUserPassword',
  async ({ userId, password }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${config.apiUrl}/api/users/${userId}/password`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ password })
        }
      );

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Add these new actions
export const createCostGroup = createAsyncThunk(
  'settings/createCostGroup',
  async (costGroupData, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${config.apiUrl}/api/settings/cost-groups`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(costGroupData)
        }
      );

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateCostGroup = createAsyncThunk(
  'settings/updateCostGroup',
  async ({ id, updates }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${config.apiUrl}/api/settings/cost-groups/${id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(updates)
        }
      );

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteCostGroup = createAsyncThunk(
  'settings/deleteCostGroup',
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${config.apiUrl}/api/settings/cost-groups/${id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message);
      }

      return { id };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Add these new actions for cost codes
export const createCostCode = createAsyncThunk(
  'settings/createCostCode',
  async (costCodeData, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${config.apiUrl}/api/settings/cost-codes`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(costCodeData)
        }
      );

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateCostCode = createAsyncThunk(
  'settings/updateCostCode',
  async ({ id, updates }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${config.apiUrl}/api/settings/cost-codes/${id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(updates)
        }
      );

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteCostCode = createAsyncThunk(
  'settings/deleteCostCode',
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${config.apiUrl}/api/settings/cost-codes/${id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message);
      }

      return { id };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Add these new actions
export const fetchCategories = createAsyncThunk(
  'settings/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${config.apiUrl}/api/settings/categories`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message);
      }

      return data.categories;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createCategory = createAsyncThunk(
  'settings/createCategory',
  async (categoryData, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${config.apiUrl}/api/settings/categories`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(categoryData)
        }
      );

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateCategory = createAsyncThunk(
  'settings/updateCategory',
  async ({ id, updates }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${config.apiUrl}/api/settings/categories/${id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(updates)
        }
      );

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteCategory = createAsyncThunk(
  'settings/deleteCategory',
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${config.apiUrl}/api/settings/categories/${id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message);
      }

      return { id };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  users: [],
  pagination: {},
  loading: false,
  error: null,
  costGroupsData: [],
  costCodesData: [],
  appLogo: null,
  categories: [],
  bannerMessage: null,
  appSettings: {
    jwtExpirationHours: 6,
    pricingDefaults: { defaultMargin: 33 }
  },
};

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchUsers
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        console.log('Users fetched:', action.payload);
        state.users = action.payload.users;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        console.error('Failed to fetch users:', action.payload);
      })

      // Handle deleteUser
      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false;
        state.users = state.users.filter(user => user._id !== action.payload.userId);
        showToast('User deleted successfully', 'success');
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })

      // Handle updateAppLogo
      .addCase(updateAppLogo.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateAppLogo.fulfilled, (state, action) => {
        state.loading = false;
        state.appLogo = action.payload.logoUrl;
        showToast('Logo updated successfully', 'success');
      })
      .addCase(updateAppLogo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })

      // Handle fetchCostGroupsData
      .addCase(fetchCostGroupsData.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCostGroupsData.fulfilled, (state, action) => {
        state.loading = false;
        state.costGroupsData = action.payload;
      })
      .addCase(fetchCostGroupsData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })

      // Handle fetchCostCodesData
      .addCase(fetchCostCodesData.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCostCodesData.fulfilled, (state, action) => {
        state.loading = false;
        state.costCodesData = action.payload;
      })
      .addCase(fetchCostCodesData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })

      // Handle updateUser
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.users.findIndex(user => user._id === action.payload.data._id);
        if (index !== -1) {
          state.users[index] = action.payload.data;
        }
        showToast('User updated successfully', 'success');
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })

      // Handle changeUserPassword
      .addCase(changeUserPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changeUserPassword.fulfilled, (state) => {
        state.loading = false;
        showToast('Password changed successfully', 'success');
      })
      .addCase(changeUserPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })

      // Handle createCostGroup
      .addCase(createCostGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCostGroup.fulfilled, (state, action) => {
        state.loading = false;
        state.costGroupsData = [...state.costGroupsData, action.payload.costGroup];
        showToast('Cost group created successfully', 'success');
      })
      .addCase(createCostGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })

      // Handle updateCostGroup
      .addCase(updateCostGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCostGroup.fulfilled, (state, action) => {
        state.loading = false;
        state.costGroupsData = state.costGroupsData.map(group => 
          group._id === action.payload.costGroup._id ? action.payload.costGroup : group
        );
        showToast('Cost group updated successfully', 'success');
      })
      .addCase(updateCostGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })

      // Handle deleteCostGroup
      .addCase(deleteCostGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCostGroup.fulfilled, (state, action) => {
        state.loading = false;
        state.costGroupsData = state.costGroupsData.filter(group => group._id !== action.payload.id);
        showToast('Cost group deleted successfully', 'success');
      })
      .addCase(deleteCostGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })

      // Handle createCostCode
      .addCase(createCostCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCostCode.fulfilled, (state, action) => {
        state.loading = false;
        state.costCodesData = [...state.costCodesData, action.payload.costCode];
        showToast('Cost code created successfully', 'success');
      })
      .addCase(createCostCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })

      // Handle updateCostCode
      .addCase(updateCostCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCostCode.fulfilled, (state, action) => {
        state.loading = false;
        state.costCodesData = state.costCodesData.map(code => 
          code._id === action.payload.costCode._id ? action.payload.costCode : code
        );
        showToast('Cost code updated successfully', 'success');
      })
      .addCase(updateCostCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })

      // Handle deleteCostCode
      .addCase(deleteCostCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCostCode.fulfilled, (state, action) => {
        state.loading = false;
        state.costCodesData = state.costCodesData.filter(code => code._id !== action.payload.id);
        showToast('Cost code deleted successfully', 'success');
      })
      .addCase(deleteCostCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })

      // Handle fetchCategories
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })

      // Handle createCategory
      .addCase(createCategory.fulfilled, (state, action) => {
        state.categories = [...state.categories, action.payload.category];
        showToast('Category created successfully', 'success');
      })

      // Handle updateCategory
      .addCase(updateCategory.fulfilled, (state, action) => {
        state.categories = state.categories.map(cat => 
          cat._id === action.payload.category._id ? action.payload.category : cat
        );
        showToast('Category updated successfully', 'success');
      })

      // Handle deleteCategory
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.categories = state.categories.filter(cat => cat._id !== action.payload.id);
        showToast('Category deleted successfully', 'success');
      })

      // Handle fetchBannerMessage
      .addCase(fetchBannerMessage.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchBannerMessage.fulfilled, (state, action) => {
        state.loading = false;
        state.bannerMessage = action.payload;
      })
      .addCase(fetchBannerMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Handle updateBannerMessage
      .addCase(updateBannerMessage.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateBannerMessage.fulfilled, (state, action) => {
        state.loading = false;
        state.bannerMessage = action.payload;
        showToast('Banner message updated successfully', 'success');
      })
      .addCase(updateBannerMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })

      // Handle fetchAppSettings
      .addCase(fetchAppSettings.fulfilled, (state, action) => {
        state.appSettings = {
          jwtExpirationHours: action.payload?.jwtExpirationHours ?? state.appSettings.jwtExpirationHours,
          pricingDefaults: action.payload?.pricingDefaults || state.appSettings.pricingDefaults
        };
      })

      // Handle updateAppSettings
      .addCase(updateAppSettings.fulfilled, (state, action) => {
        state.appSettings = {
          jwtExpirationHours: action.payload?.jwtExpirationHours ?? state.appSettings.jwtExpirationHours,
          pricingDefaults: action.payload?.pricingDefaults || state.appSettings.pricingDefaults
        };
      });
  }
});

export const { clearError } = settingsSlice.actions;
export default settingsSlice.reducer; 